import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
    Upload,
    CheckCircle,
    X,
    Brain,
    Loader2,
} from "lucide-react";
import PhoneInput, {
    isValidPhoneNumber,
    getCountries,
} from "react-phone-number-input";
import CustomDropdown from "../../components/common/CustomDropdown";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";
import { languages } from "../../utils/languages";
import "../../App.css";


const CustomATSForm = ({
    linkToken,
    jobDetails,
    branding,
    isWhiteLabelingEnable,
    host,
    setApplicationSubmitted,
    setApplicationClosed,
    enableCustomATSQuestions
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(0);
    const [defaultCountry, setDefaultCountry] = useState(null);

    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 3;

    // Form state - Mandatory fields
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [experience, setExperience] = useState("");
    const [noticePeriod, setNoticePeriod] = useState("");
    const [expectedjoining, setExpectedJoining] = useState("");

    // Optional fields
    const [sourceOfApplication, setSourceOfApplication] = useState("");
    const [otherSourceDetails, setOtherSourceDetails] = useState("");

    // Resume upload
    const [resume, setResume] = useState(null);
    const [resumeName, setResumeName] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);

    // AI Resume parsing states
    const [autoFilledFields, setAutoFilledFields] = useState([]);

    // Additional information fields (optional)
    const [currentCompany, setCurrentCompany] = useState("");
    const [designation, setDesignation] = useState("");
    const [salaryExpectation, setSalaryExpectation] = useState("");
    const [lastSalary, setLastSalary] = useState("");
    const [currentBenefits, setCurrentBenefits] = useState([]);
    const [visaStatus, setVisaStatus] = useState("");
    const [otherVisaDetails, setOtherVisaDetails] = useState("");
    const [languagesKnown, setLanguagesKnown] = useState("");
    const [age, setAge] = useState("");
    const [collegeYears, setCollegeYears] = useState("");
    const [familyBackground, setFamilyBackground] = useState("");
    const [gender, setGender] = useState("");
    const [relativesInCompany, setRelativesInCompany] = useState("");
    const [relativeName, setRelativeName] = useState("");
    const [relativeDesignation, setRelativeDesignation] = useState("");
    const [referralName, setReferralName] = useState("");
    const [referralDesignation, setReferralDesignation] = useState("");
    const [maritalStatus, setMaritalStatus] = useState("");
    const [errors, setErrors] = useState({});

    // Red Asterisk for mandatory fields
    const RedStar = () => (
        <span className="text-red-500">*</span>
    );

    const numberInputStyles = `
    input[type="number"] {
        -moz-appearance: textfield;           /* Firefox */
        appearance: textfield;                /* Standard */
    }

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    `;

    // Options
    const visaStatusOptions = [
        // "US Citizen",
        // "Permanent Resident",
        // "H-1B",
        // "OPT",
        // "No Visa Required",
        // "Other",
        "Visit Visa",
        "Spouse Visa",
        "Employment Visa",
        "Cancelled Visa",
        "Over Stay",
        "Parental Visa",
        "Golden Visa"
    ];

    const collegeYearsOptions = [
        // "Less than 1 year",
        // "1-2 years",
        // "2-3 years",
        // "3-4 years",
        // "4+ years",
        "High School",
        "1 Year",
        "2 Years",
        "3 Years",
        "4 Years",
        "4+ Years",
        "Master's Degree"
    ];

    const experienceOptions = [
        "0-1 years",
        "1-2 years",
        "2-3 years",
        "3-5 years",
        "5-7 years",
        "7-10 years",
        "10+ years",
    ];

    const noticePeriodOptions = [
        "Fresher",
        "Immediate",
        "15 days",
        "30 days",
        "45 days",
        "60 days",
        "90 days",
        "Serving notice period",
    ];

    const expectedJoiningDateOptions = [
        // "Fresher",
        "Immediate",
        // "15 days",
        // "30 days",
        // "45 days",
        // "60 days",
        // "90 days",
        // "Serving notice period",
        "Less than 1 month",
        "More than 1 month"
    ];

    const sourceOptions = [
        "LinkedIn",
        "Naukri.com",
        "Indeed",
        "Monster.com",
        "Company Website",
        "Employee Referral",
        "Recruiter Agency",
        "Social Media",
        // "Other",
    ];

    const genderoptions = [
        "Male",
        "Female",
        // "Non-Binary",
        "Other",
    ];

    const maritalStatusOptions = [
        "Single",
        "Married",
        "Divorced",
    ];

    const benefitsOptions = [
        "All",
        "Medical Insurance",
        "Family Air Ticket",
        "Yearly Bonus",
    ];

    const validateFields = (step) => {
        const newErrors = {};

        if (step === 1) {
            if (!name.trim()) newErrors.name = "Full name is required";
            if (!email.trim()) {
                newErrors.email = "Email is required";
            } else {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                    newErrors.email = "Please enter a valid email address";
                }
            }
            if (!mobile || !isValidPhoneNumber(mobile)) {
                newErrors.mobile = "Valid mobile number is required";
            }
            if (!city.trim()) newErrors.city = "City is required";
            if (!state.trim()) newErrors.state = "State is required";
            if (!age) {
                newErrors.age = "Age is required";
            } else if (parseInt(age) < 0 || parseInt(age) > 100) {
                newErrors.age = "Age must be between 0 and 100";
            }
            if (!resume) newErrors.resume = "Resume is required";
        }

        if (step === 2) {
            if (!currentCompany.trim()) newErrors.currentCompany = "Current company is required";
            if (!designation.trim()) newErrors.designation = "Designation is required";
            if (!experience) newErrors.experience = "Experience is required";
            if (!lastSalary.trim()) newErrors.lastSalary = "Current salary is required";
            if (!salaryExpectation.trim()) newErrors.salaryExpectation = "Salary expectation is required";
            if (!noticePeriod) newErrors.noticePeriod = "Notice period is required";
            if (!expectedjoining) newErrors.expectedjoining = "Expected joining date is required";
        }

        if (step === 3) {
            if (!gender) newErrors.gender = "Gender is required";
            if (!languagesKnown.trim()) newErrors.languagesKnown = "Languages known is required";
            if (!visaStatus) newErrors.visaStatus = "Visa status is required";
            if (visaStatus === "Other" && !otherVisaDetails.trim()) {
                newErrors.otherVisaDetails = "Please specify your visa status";
            }
            if (!sourceOfApplication) newErrors.sourceOfApplication = "Source of application is required";
            if (sourceOfApplication === "Other" && !otherSourceDetails.trim()) {
                newErrors.otherSourceDetails = "Please specify the source";
            }
            if (sourceOfApplication === "Employee Referral") {
                if (!referralName.trim()) {
                    newErrors.referralName = "Employee Name is required";
                }
                if (!referralDesignation.trim()) {
                    newErrors.referralDesignation = "Employee Designation is required";
                }
            }
        }

        return newErrors;
    };

    const nextStep = () => {
        const stepErrors = validateFields(currentStep);
        if (Object.keys(stepErrors).length > 0) {
            setErrors(stepErrors);
            // Optionally scroll to first error
            const firstErrorField = Object.keys(stepErrors)[0];
            const element = document.getElementsByName(firstErrorField)[0];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            setErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    useEffect(() => {
        const detectCountry = async () => {
            try {
                const { data } = await axios.get("https://ipapi.co/json/", {
                    timeout: 3000,
                });
                const code = data.country_code?.toUpperCase();
                if (code && getCountries().includes(code)) {
                    setDefaultCountry(code);
                }
            } catch (e) {
                console.warn("IP geolocation failed, using fallback");
            }
        };
        detectCountry();
    }, []);

    const validateFile = (file) => {
        const maxSize = 3 * 1024 * 1024; // 3MB
        const allowedTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/msword",
        ];
        const allowedExtensions = [".pdf", ".docx", ".doc"];

        if (!file || !file.name) {
            return { valid: false, error: "No file provided" };
        }

        if (file.size > maxSize) {
            return { valid: false, error: "File size exceeds 3MB limit" };
        }

        const fileName = file.name.toLowerCase();
        const hasValidExtension = allowedExtensions.some((ext) =>
            fileName.endsWith(ext)
        );

        if (!allowedTypes.includes(file.type) && !hasValidExtension) {
            return {
                valid: false,
                error: "Invalid file type. Only PDF, DOCX, and DOC files are supported",
            };
        }

        return { valid: true, error: null };
    };

    const resetExtractionState = () => {
        setIsExtracting(false);
        setExtractionProgress(0);
        setAutoFilledFields([]);
    };

    const extractResumeData = async (file) => {
        setIsExtracting(true);
        setExtractionProgress(0);

        const progressInterval = setInterval(() => {
            setExtractionProgress((prev) => {
                if (prev >= 90) return prev;
                return prev + 10;
            });
        }, 300);

        try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await axios.post(`${host}/the_parse_resume`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const extractedData = response.data;

            clearInterval(progressInterval);
            setExtractionProgress(100);

            const filledFields = [];

            if (extractedData.name && !name.trim()) {
                setName(extractedData.name);
                filledFields.push("Name");
            }
            if (extractedData.email && !email.trim()) {
                setEmail(extractedData.email);
                filledFields.push("Email");
            }
            if (extractedData.mobile && !mobile.trim()) {
                setMobile(extractedData.mobile);
                filledFields.push("Mobile");
            }
            if (extractedData.currentLocation) {
                if (!city.trim() && !state.trim()) {
                    const [extractedCity, extractedState] =
                        extractedData.currentLocation.split(",");
                    setCity(extractedCity?.trim() || "");
                    setState(extractedState?.trim() || "");
                    filledFields.push("Location");
                }
            }
            if (
                extractedData.experience &&
                !experience &&
                experienceOptions.includes(extractedData.experience)
            ) {
                setExperience(extractedData.experience);
                filledFields.push("Experience");
            }

            setAutoFilledFields(filledFields);
        } catch (error) {
            console.error("Resume extraction failed:", error);
            clearInterval(progressInterval);
            setExtractionProgress(0);
            toast.error(
                error.response?.data?.detail ||
                "Could not extract information from resume."
            );
        } finally {
            setTimeout(() => {
                setIsExtracting(false);
            }, 500);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            setResume(null);
            setResumeName("");
            resetExtractionState();
            return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
            toast.error(validation.error);
            setResume(null);
            setResumeName("");
            return;
        }

        setResume(file);
        setResumeName(file.name);
        setErrors(prev => ({ ...prev, resume: undefined }));
        await extractResumeData(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];

        if (file) {
            const validation = validateFile(file);
            if (!validation.valid) {
                toast.error(validation.error);
                return;
            }

            setResume(file);
            setResumeName(file.name);
            await extractResumeData(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const removeFile = () => {
        window.location.reload();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final validation check across all steps
        const step1Errors = validateFields(1);
        if (Object.keys(step1Errors).length > 0) {
            setErrors(step1Errors);
            setCurrentStep(1);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const step2Errors = validateFields(2);
        if (Object.keys(step2Errors).length > 0) {
            setErrors(step2Errors);
            setCurrentStep(2);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const step3Errors = validateFields(3);
        if (Object.keys(step3Errors).length > 0) {
            setErrors(step3Errors);
            setCurrentStep(3);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }


        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("email", email.trim());
            formData.append("mobile", mobile.trim());
            formData.append("currentLocation", `${city.trim()}, ${state.trim()}`);
            formData.append("experience", experience);
            formData.append("noticePeriod", noticePeriod);
            formData.append("expected_joining_date", expectedjoining);
            formData.append("sourceOfApplication", sourceOfApplication.trim());

            // Combine referral name and designation for Employee Referral
            const finalOtherSourceDetails = sourceOfApplication === "Employee Referral" && referralName.trim() && referralDesignation.trim()
                ? `${referralName.trim()} - ${referralDesignation.trim()}`
                : otherSourceDetails.trim();
            formData.append("otherSourceDetails", finalOtherSourceDetails);
            formData.append("resume", resume);

            if (enableCustomATSQuestions) {
                formData.append("currentCompany", currentCompany.trim());
                formData.append("designation", designation.trim());
                formData.append("salaryExpectation", salaryExpectation.trim());
                formData.append("lastSalary", lastSalary.trim());
                formData.append("currentBenefits", currentBenefits.join(", "));

                const finalVisaStatus = visaStatus === "Other" ? otherVisaDetails.trim() : visaStatus.trim();
                formData.append("visaStatus", finalVisaStatus);

                formData.append("languagesKnown", languagesKnown.trim());
                formData.append("age", age);
                formData.append("collegeYears", collegeYears);
                formData.append("familyBackground", familyBackground.trim());
                formData.append("gender", gender.trim());
                const combinedRelatives = relativeName.trim() && relativeDesignation.trim() ? `${relativeName.trim()} - ${relativeDesignation.trim()}` : "";
                formData.append("relativesInCompany", combinedRelatives);
                formData.append("marital_status", maritalStatus.trim());
            }

            const response = await axios.post(
                `${host}/public/apply/${linkToken}`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            setApplicationSubmitted(true);
            toast.success("Application submitted successfully!");
        } catch (error) {
            console.error("Error submitting application:", error);
            if (error.response) {
                const status = error.response.status;
                const detail = error.response.data.detail || "Unknown error";
                if (status === 400) {
                    toast.error(detail);
                } else if (status === 404) {
                    if (detail.includes("no longer accepting applications")) {
                        setApplicationClosed(true);
                    } else {
                        toast.error("This job link is no longer available.");
                    }
                } else {
                    toast.error(`Error: ${detail}`);
                }
            } else if (error.message.includes("Network Error")) {
                toast.error(
                    "Network error. Please check your connection and try again."
                );
            } else {
                toast.error("An unexpected error occurred. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Step 1 validation
    const isStep1Valid = () => {
        return (
            name.trim() !== "" &&
            email.trim() !== "" &&
            mobile && isValidPhoneNumber(mobile) &&
            city.trim() !== "" &&
            state.trim() !== "" &&
            age.trim() !== "" &&
            !!resume                     // resume is required
        );
    };

    // Step 2 validation
    const isStep2Valid = () => {
        return (
            currentCompany.trim() !== "" &&
            designation.trim() !== "" &&
            !!experience &&
            lastSalary.trim() !== "" &&
            salaryExpectation.trim() !== "" &&
            !!noticePeriod && !!expectedjoining
        );
    };

    // Step 3 validation (only if submitting)
    const isStep3Valid = () => {
        return (
            !!visaStatus && (visaStatus !== "Other" || otherVisaDetails.trim() !== "") &&
            sourceOfApplication &&
            (sourceOfApplication !== "Other" || otherSourceDetails.trim() !== "")
            // languagesKnown, collegeYears etc. are optional → not required here
        );
    };

    const canGoNext = () => {
        if (currentStep === 1) return isStep1Valid();
        if (currentStep === 2) return isStep2Valid();
        if (currentStep === 3) return isStep3Valid();
        return false;
    };

    return (
        <div
            className={`rounded-3xl p-8 shadow-sm ${isWhiteLabelingEnable ? "bg-white backdrop-blur-sm" : "glass-effect"
                }`}
        >
            <h3
                className={`text-xl font-medium mb-6 ${isWhiteLabelingEnable ? "text-gray-700" : "text-[#C1A0D0]"
                    }`}
            >
                Submit Your Application
            </h3>
            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Progress Indicator */}
                <div className="relative mb-12">
                    <div className={`absolute ${isWhiteLabelingEnable ? "bg-gray-700" : "bg-gray-600"} h-[2px] top-28 sm:top-24 bottom-0 left-0 right-0 rounded-full overflow-hidden`}>
                        {/* Progress Fill Bar - Gradient from pink to blue */}
                        <div
                            className="h-[2px] bg-gradient-to-r from-pink-500 to-blue-500 transition-all duration-500 ease-in-out rounded-full"
                            style={{
                                width:
                                    currentStep === 1 ? "33%" :
                                        currentStep === 2 ? "66%" :
                                            "100%"
                            }}
                        />
                    </div>

                    <div className="relative flex justify-between items-center">
                        {[
                            { step: 1, label: "Personal Information" },
                            { step: 2, label: "Work Experience" },
                            { step: 3, label: "Additional Information" }
                        ].map((item, index) => (
                            <div key={item.step} className="flex flex-col items-center relative z-10">
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-md font-[2px] transition-all duration-300 ${currentStep >= item.step
                                        ? "bg-transparent text-green-600 border-[2px] border-green-600"
                                        : "bg-transparent text-gray-600 border-[2px] border-gray-600"
                                        }`}
                                >
                                    {currentStep > item.step ? item.step : item.step}
                                </div>

                                <p
                                    className={`mt-3 text-[10px] sm:text-base md:text-lg font-medium pl-2 transition-all duration-300 ${currentStep > item.step
                                        ? "text-green-600"
                                        : currentStep === item.step
                                            ? "text-gray-600 font-bold"
                                            : "text-gray-500"
                                        }`}
                                >
                                    {item.label}
                                </p>
                            </div>
                        ))}

                        {/* Arrows between steps */}
                        <div className="absolute top-6 left-16 right-16 flex justify-between pointer-events-none">
                            {/* <span className="text-gray-500 text-xl">›</span>
            <span className="text-gray-500 text-xl">›</span> */}
                        </div>
                    </div>
                </div>

                {/* STEP 1: Resume Upload + Personal Information */}
                {currentStep === 1 && (
                    <div className="space-y-8">
                        {/* Resume Upload */}
                        <div className="space-y-4">
                            <h4
                                className={`text-md font-medium border-b pb-2 ${isWhiteLabelingEnable
                                    ? "border-gray-300 text-gray-600"
                                    : "text-white border-b border-gray-600"
                                    }`}
                            >
                                Upload Resume
                            </h4>
                            <div>
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept=".pdf,.docx,.doc"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        id="resumeUpload"
                                    />
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        className={`border-2 border-dashed rounded-lg cursor-pointer transition-colors
                                            ${errors.resume && !resumeName && !isExtracting ? "border-red-500 bg-red-50/10" : ""}
                                ${!isWhiteLabelingEnable
                                                ? "border-[#a88eb4]"
                                                : "border-gray-500 hover:border-gray-500"
                                            } 
                                ${resumeName ? "border-green-500 bg-green-500/10" : ""}
                                py-6 sm:py-8 md:py-10 lg:py-0   /* ← control vertical size here */
                            `}
                                    >
                                        {isExtracting ? (
                                            <div className="p-4">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <Brain
                                                        className={`w-6 h-6 animate-pulse ${isWhiteLabelingEnable ? "text-gray-700" : "text-[#C1A0D0]"
                                                            }`}
                                                    />
                                                    <p
                                                        className={`font-medium ${isWhiteLabelingEnable ? "text-gray-700" : "text-white"
                                                            }`}
                                                    >
                                                        Extracting data from {resumeName}...
                                                    </p>
                                                </div>
                                                <div className="w-full bg-gray-600 rounded-full h-1">
                                                    <div
                                                        className={`${isWhiteLabelingEnable
                                                            ? "bg-[#1569c4]"
                                                            : "bg-gradient-to-r from-pink-500 to-blue-500"
                                                            } h-1 rounded-full transition-all duration-300`}
                                                        style={{ width: `${extractionProgress}%` }}
                                                    ></div>
                                                </div>
                                                <p className="text-xs text-gray-600 mt-2">
                                                    {extractionProgress === 100
                                                        ? "Data extracted successfully!"
                                                        : "Processing resume..."}
                                                </p>
                                            </div>
                                        ) : resumeName ? (
                                            <div className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <CheckCircle className="w-6 h-6 text-green-400" />
                                                        <p
                                                            className={`font-medium ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"
                                                                }`}
                                                        >
                                                            {resumeName}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={removeFile}
                                                        className="p-1 rounded-full hover:bg-gray-500/30 transition-colors"
                                                    >
                                                        <X
                                                            className={`w-4 h-4 ${isWhiteLabelingEnable ? "text-gray-500" : "text-white"
                                                                }`}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label
                                                htmlFor="resumeUpload"
                                                className="block p-8 text-center cursor-pointer light"
                                            >
                                                {/* <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" /> */}
                                                <p
                                                    className={`mb-1 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"
                                                        }`}
                                                >
                                                    Drag and drop your resume here
                                                </p>
                                                {/* <p className="text-sm text-gray-300 mb-2">or</p> */}
                                                <span
                                                    className={`inline-block px-3 py-1 rounded-lg text-sm font-semibold transition-colors hover:scale-105 ${isWhiteLabelingEnable
                                                        ? "bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-full px-6 py-2 text-sm font-semibold shadow-md hover:from-pink-600 hover:to-indigo-600 transition-all duration-300 hover:scale-105"
                                                        : "bg-[#C1A0D0]/20 text-[#C1A0D0] hover:bg-[#C1A0D0]/30"
                                                        }`}
                                                >
                                                    Browse Files
                                                </span>
                                                <p className={`mt-2 text-xs ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-600"}`}>
                                                    PDF, DOCX, DOC files only (Max 3MB)
                                                </p>
                                            </label>
                                        )}
                                    </div>
                                    {errors.resume && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.resume}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Personal Information */}
                        <div className="space-y-4">
                            <h4
                                className={`text-md font-medium border-b pb-2 ${isWhiteLabelingEnable
                                    ? "border-gray-300 text-gray-600"
                                    : "text-white border-b border-gray-600"
                                    }`}
                            >
                                Personal Information
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Full Name <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={name}
                                        onChange={(e) => {
                                            setName(e.target.value);
                                            if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.name ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter your full name"
                                        required
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Email Address <RedStar />
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.email ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="your.email@example.com"
                                        required
                                    />
                                    {errors.email && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Mobile Number <RedStar />
                                    </label>
                                    <div className="w-full">
                                        <PhoneInput
                                            international
                                            name="mobile"
                                            defaultCountry={defaultCountry}
                                            value={mobile}
                                            onChange={(value) => {
                                                setMobile(value || "");
                                                if (errors.mobile) setErrors(prev => ({ ...prev, mobile: undefined }));
                                            }}
                                            className={`w-full px-4 py-0 bg-transparent border ${errors.mobile ? "border-red-500" : "border-gray-400"} ${mobile ? isWhiteLabelingEnable ? "text-textSecondary" :"text-[#bdbdbd]" : isWhiteLabelingEnable ? "text-gray-400" :"text-[#bdbdbd]" } rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors`}
                                            // style={{ color: isWhiteLabelingEnable ? '#374151' : '#d1d5db' }}
                                        />
                                        {(errors.mobile || (mobile && !isValidPhoneNumber(mobile))) && (
                                            <p className="text-red-500 text-xs mt-1 pl-1">
                                                {errors.mobile || "Please enter a valid phone number (include country code)"}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        City <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={city}
                                        onChange={(e) => {
                                            setCity(e.target.value);
                                            if (errors.city) setErrors(prev => ({ ...prev, city: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.city ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter your city"
                                        required
                                    />
                                    {errors.city && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.city}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        State <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={state}
                                        onChange={(e) => {
                                            setState(e.target.value);
                                            if (errors.state) setErrors(prev => ({ ...prev, state: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.state ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter your state"
                                        required
                                    />
                                    {errors.state && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.state}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Age <RedStar />
                                    </label>
                                    <style>{numberInputStyles}</style>
                                    <input
                                        type="number"
                                        name="age"
                                        value={age}
                                        onChange={(e) => {
                                            setAge(e.target.value);
                                            if (errors.age) setErrors(prev => ({ ...prev, age: undefined }));
                                        }}
                                        min="0"
                                        max="100"
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.age ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter your age"
                                        required
                                    />
                                    {errors.age && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.age}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Next Button */}
                        <div className="flex justify-end pt-4 light">
                            {/* STEP 1 - Next button */}
                            <button
                                type="button"
                                onClick={nextStep}
                                className={`font-medium transition-all ${isWhiteLabelingEnable
                                    ? "button"
                                    : "relative inline-flex rounded-full p-[2px] bg-[linear-gradient(45deg,#0ce39a,#69007f,#fc0987)]"
                                    }`}
                            >
                                {
                                    isWhiteLabelingEnable ? (
                                        "Next"
                                    ) : (
                                        <span className="flex items-center justify-center rounded-full bg-[#200627] px-6 py-3 text-white">
                                            Next
                                        </span>
                                    )}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 2: Work Experience */}
                {currentStep === 2 && (
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <h4
                                className={`text-md font-medium border-b pb-2 ${isWhiteLabelingEnable
                                    ? "border-gray-300 text-gray-600"
                                    : "text-white border-b border-gray-600"
                                    }`}
                            >
                                Work Experience
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Current Company <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="currentCompany"
                                        value={currentCompany}
                                        onChange={(e) => {
                                            setCurrentCompany(e.target.value);
                                            if (errors.currentCompany) setErrors(prev => ({ ...prev, currentCompany: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.currentCompany ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter company name"
                                        required
                                    />
                                    {errors.currentCompany && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.currentCompany}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Designation <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="designation"
                                        value={designation}
                                        onChange={(e) => {
                                            setDesignation(e.target.value);
                                            if (errors.designation) setErrors(prev => ({ ...prev, designation: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.designation ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="Enter your job title"
                                        required
                                    />
                                    {errors.designation && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.designation}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Years of Experience <RedStar />
                                    </label>
                                    <CustomDropdown
                                        options={experienceOptions}
                                        name="experience"
                                        value={experience}
                                        onChange={(val) => {
                                            setExperience(val);
                                            if (errors.experience) setErrors(prev => ({ ...prev, experience: undefined }));
                                        }}
                                        placeholder="Select experience level"
                                        required
                                        isWhiteLabelingEnable={isWhiteLabelingEnable}
                                        error={!!errors.experience}
                                    />
                                    {errors.experience && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.experience}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Current Salary Per Annum <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="lastSalary"
                                        value={lastSalary}
                                        onChange={(e) => {
                                            setLastSalary(e.target.value);
                                            if (errors.lastSalary) setErrors(prev => ({ ...prev, lastSalary: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.lastSalary ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="e.g., 45000 AED"
                                        required
                                    />
                                    {errors.lastSalary && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.lastSalary}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Salary Expectation Per Annum <RedStar />
                                    </label>
                                    <input
                                        type="text"
                                        name="salaryExpectation"
                                        value={salaryExpectation}
                                        onChange={(e) => {
                                            setSalaryExpectation(e.target.value);
                                            if (errors.salaryExpectation) setErrors(prev => ({ ...prev, salaryExpectation: undefined }));
                                        }}
                                        className={`w-full px-4 py-3 bg-transparent border ${errors.salaryExpectation ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                        placeholder="e.g., 50000-60000 AED"
                                        required
                                    />
                                    {errors.salaryExpectation && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.salaryExpectation}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Notice Period <RedStar />
                                    </label>
                                    <CustomDropdown
                                        options={noticePeriodOptions}
                                        name="noticePeriod"
                                        value={noticePeriod}
                                        onChange={(val) => {
                                            setNoticePeriod(val);
                                            if (errors.noticePeriod) setErrors(prev => ({ ...prev, noticePeriod: undefined }));
                                        }}
                                        placeholder="Select notice period"
                                        required
                                        isWhiteLabelingEnable={isWhiteLabelingEnable}
                                        error={!!errors.noticePeriod}
                                    />
                                    {errors.noticePeriod && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.noticePeriod}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Expected Joining Date <RedStar />
                                    </label>
                                    <CustomDropdown
                                        options={expectedJoiningDateOptions}
                                        name="expectedjoining"
                                        value={expectedjoining}
                                        onChange={(val) => {
                                            setExpectedJoining(val);
                                            if (errors.expectedjoining) setErrors(prev => ({ ...prev, expectedjoining: undefined }));
                                        }}
                                        placeholder="Select expected joining period"
                                        required
                                        isWhiteLabelingEnable={isWhiteLabelingEnable}
                                        error={!!errors.expectedjoining}
                                    />
                                    {errors.expectedjoining && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.expectedjoining}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Back & Next Buttons */}
                        <div className="flex justify-between pt-4 light">
                            <button
                                type="button"
                                onClick={prevStep}
                                className={`font-medium transition-all ${isWhiteLabelingEnable
                                    ? " border-btn !text-gray-600"
                                    : "relative inline-flex rounded-full p-[2px] bg-[linear-gradient(45deg,#0ce39a,#69007f,#fc0987)]"
                                    }`}
                            >
                                {isWhiteLabelingEnable ? (
                                    "Back"
                                ) : (
                                    <span className="flex items-center justify-center rounded-full bg-[#200627] px-6 py-3 text-white">
                                        Back
                                    </span>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={nextStep}
                                className={`font-medium transition-all ${isWhiteLabelingEnable
                                    ? "button"
                                    : "relative inline-flex rounded-full p-[2px] bg-[linear-gradient(45deg,#0ce39a,#69007f,#fc0987)]"
                                    }`}
                            >
                                {isWhiteLabelingEnable ? (
                                    "Next"
                                ) : (
                                    <span className="flex items-center justify-center rounded-full bg-[#200627] px-6 py-3 text-white">
                                        Next
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3: Additional Information */}
                {
                    currentStep === 3 && (
                        <div className="space-y-8">
                            <div className="space-y-4">
                                <h4
                                    className={`text-md font-medium border-b pb-2 ${isWhiteLabelingEnable
                                        ? "border-gray-300 text-gray-600"
                                        : "text-white border-b border-gray-600"
                                        }`}
                                >
                                    Additional Information
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            Gender <RedStar />
                                        </label>
                                        <CustomDropdown
                                            options={genderoptions}
                                            value={gender}
                                            onChange={(e) => {
                                                setGender(e);
                                                if (errors.gender) setErrors(prev => ({ ...prev, gender: undefined }));
                                            }}
                                            placeholder="Select Gender"
                                            required
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            error={!!errors.gender}
                                        />
                                        {errors.gender && (
                                            <p className="text-red-500 text-xs mt-1 pl-1">
                                                {errors.gender}
                                            </p>
                                        )}

                                        {visaStatus === "Other" && (
                                            <div className="mt-3">
                                                <input
                                                    type="text"
                                                    name="otherVisaDetails"
                                                    value={otherVisaDetails}
                                                    onChange={(e) => {
                                                        setOtherVisaDetails(e.target.value);
                                                        if (errors.otherVisaDetails) setErrors(prev => ({ ...prev, otherVisaDetails: undefined }));
                                                    }}
                                                    className={`w-full px-4 py-3 bg-transparent border ${errors.otherVisaDetails ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                    placeholder="Please specify your visa status"
                                                    maxLength={150}
                                                    required={visaStatus === "Other"} // makes it required only when Other is selected
                                                />
                                                {errors.otherVisaDetails && (
                                                    <p className="text-red-500 text-xs mt-1 pl-1">
                                                        {errors.otherVisaDetails}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            Marital Status
                                        </label>
                                        <CustomDropdown
                                            options={maritalStatusOptions}
                                            value={maritalStatus}
                                            onChange={(e) => {
                                                setMaritalStatus(e);
                                                if (errors.maritalStatus) setErrors(prev => ({ ...prev, maritalStatus: undefined }));
                                            }}
                                            placeholder="Select marital status"
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            error={!!errors.maritalStatus}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            Languages Known <RedStar />
                                        </label>
                                        <MultiSelectDropdown
                                            options={languages}
                                            name="languagesKnown"
                                            value={languagesKnown}
                                            onChange={(val) => {
                                                setLanguagesKnown(val);
                                                if (errors.languagesKnown) setErrors(prev => ({ ...prev, languagesKnown: undefined }));
                                            }}
                                            placeholder="Select languages known"
                                            searchPlaceholder="Search languages..."
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            required
                                            maxSelection={4}
                                            error={!!errors.languagesKnown}
                                        />
                                        {errors.languagesKnown && (
                                            <p className="text-red-500 text-xs mt-1 pl-1">
                                                {errors.languagesKnown}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            No. of years in college
                                        </label>
                                        <CustomDropdown
                                            options={collegeYearsOptions}
                                            value={collegeYears}
                                            onChange={(e) => {
                                                setCollegeYears(e);
                                                if (errors.collegeYears) setErrors(prev => ({ ...prev, collegeYears: undefined }));
                                            }}
                                            placeholder="Select years"
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            required
                                            error={!!errors.collegeYears}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            Visa Status <RedStar />
                                        </label>
                                        <CustomDropdown
                                            options={visaStatusOptions}
                                            name="visaStatus"
                                            value={visaStatus}
                                            onChange={(val) => {
                                                setVisaStatus(val);
                                                if (errors.visaStatus) setErrors(prev => ({ ...prev, visaStatus: undefined }));
                                            }}
                                            placeholder="Select visa status"
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            required
                                            error={!!errors.visaStatus}
                                        />
                                        {errors.visaStatus && (
                                            <p className="text-red-500 text-xs mt-1 pl-1">
                                                {errors.visaStatus}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                            Current Benefits <RedStar />
                                        </label>
                                        <MultiSelectDropdown
                                            options={benefitsOptions}
                                            value={currentBenefits.join(", ")}
                                            onChange={(val) => {
                                                const selectedItems = val ? val.split(", ").map(item => item.trim()).filter(Boolean) : [];
                                                setCurrentBenefits(selectedItems);
                                                if (errors.currentBenefits) setErrors(prev => ({ ...prev, currentBenefits: undefined }));
                                            }}
                                            placeholder="Select current benefits"
                                            searchPlaceholder="Search benefits..."
                                            isWhiteLabelingEnable={isWhiteLabelingEnable}
                                            required
                                            error={!!errors.currentBenefits}
                                        />
                                    </div>
                                </div>

                                {/* Relatives Information */}
                                <div className="space-y-4">
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        Relatives in Company
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <input
                                                type="text"
                                                value={relativeName}
                                                onChange={(e) => setRelativeName(e.target.value)}
                                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                placeholder="Enter relative's name"
                                            />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                value={relativeDesignation}
                                                onChange={(e) => setRelativeDesignation(e.target.value)}
                                                className={`w-full px-4 py-3 bg-transparent border border-gray-400 rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                placeholder="Enter relative's designation"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* How did you hear about us? */}
                                <div className="space-y-4">
                                    <label className={`block text-sm font-medium mb-2 ${isWhiteLabelingEnable ? "text-gray-600" : "text-gray-300"}`}>
                                        How did you hear about us? <RedStar />
                                    </label>
                                    <CustomDropdown
                                        options={sourceOptions}
                                        name="sourceOfApplication"
                                        value={sourceOfApplication}
                                        onChange={(val) => {
                                            setSourceOfApplication(val);
                                            if (errors.sourceOfApplication) setErrors(prev => ({ ...prev, sourceOfApplication: undefined }));
                                        }}
                                        placeholder="Select Source"
                                        required
                                        isWhiteLabelingEnable={isWhiteLabelingEnable}
                                        error={!!errors.sourceOfApplication}
                                    />
                                    {errors.sourceOfApplication && (
                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                            {errors.sourceOfApplication}
                                        </p>
                                    )}
                                    {sourceOfApplication === "Other" && (
                                        <div className="mt-3">
                                            <input
                                                type="text"
                                                name="otherSourceDetails"
                                                value={otherSourceDetails}
                                                onChange={(e) => {
                                                    setOtherSourceDetails(e.target.value);
                                                    if (errors.otherSourceDetails) setErrors(prev => ({ ...prev, otherSourceDetails: undefined }));
                                                }}
                                                className={`w-full px-4 py-3 bg-transparent border ${errors.otherSourceDetails ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                placeholder="Please specify"
                                                maxLength={200}
                                            />
                                            {errors.otherSourceDetails && (
                                                <p className="text-red-500 text-xs mt-1 pl-1">
                                                    {errors.otherSourceDetails}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {sourceOfApplication === "Employee Referral" && (
                                        <div className="mt-3">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <input
                                                        type="text"
                                                        value={referralName}
                                                        onChange={(e) => {
                                                            setReferralName(e.target.value);
                                                            if (errors.referralName) setErrors(prev => ({ ...prev, referralName: undefined }));
                                                        }}
                                                        className={`w-full px-4 py-3 bg-transparent border ${errors.referralName ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                        placeholder="Employee Name"
                                                        maxLength={100}
                                                    />
                                                    {errors.referralName && (
                                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                                            {errors.referralName}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        value={referralDesignation}
                                                        onChange={(e) => {
                                                            setReferralDesignation(e.target.value);
                                                            if (errors.referralDesignation) setErrors(prev => ({ ...prev, referralDesignation: undefined }));
                                                        }}
                                                        className={`w-full px-4 py-3 bg-transparent border ${errors.referralDesignation ? "border-red-500" : "border-gray-400"} rounded-lg placeholder-gray-400 focus:outline-none focus:border-[#C1A0D0] transition-colors ${isWhiteLabelingEnable ? "text-gray-700" : "text-gray-300"}`}
                                                        placeholder="Employee Designation"
                                                        maxLength={100}
                                                    />
                                                    {errors.referralDesignation && (
                                                        <p className="text-red-500 text-xs mt-1 pl-1">
                                                            {errors.referralDesignation}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Back & Submit Buttons */}
                            <div className="flex justify-between pt-4 light">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className={`font-medium transition-all ${isWhiteLabelingEnable
                                        ? "border-btn !text-gray-600"
                                        : "relative inline-flex rounded-full p-[2px] bg-[linear-gradient(45deg,#0ce39a,#69007f,#fc0987)]"
                                        }`}
                                >
                                    {isWhiteLabelingEnable ? (
                                        "Back"
                                    ) : (
                                        <span className="flex items-center justify-center rounded-full bg-[#200627] px-6 py-3 text-white">
                                            Back
                                        </span>
                                    )}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`font-medium transition-all ${isWhiteLabelingEnable
                                        ? "button"
                                        : "relative inline-flex rounded-3xl p-[2px] bg-[linear-gradient(45deg,#0ce39a,#69007f,#fc0987)]"
                                        } ${isSubmitting ? "cursor-not-allowed opacity-80" : ""}`}
                                >
                                    {isSubmitting ? (
                                        isWhiteLabelingEnable ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Submitting...
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center rounded-3xl bg-[#200627] px-4 py-3 text-white gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Submitting...
                                            </span>
                                        )
                                    ) : isWhiteLabelingEnable ? (
                                        "Submit"
                                    ) : (
                                        <span className="flex items-center justify-center rounded-3xl bg-[#200627] px-4 py-3 text-white">
                                            Submit
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>
                    )
                }
            </form >
            {/* <div className="py-10 mt-6 text-center text-xs text-gray-400">
                <p>Your application will be submitted for review.</p>
                <p className="mt-2">Powered by GenieHire</p>
            </div> */}
        </div >
    );
};

export default CustomATSForm;
